import { useState, useEffect, useCallback } from 'react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { Search, ChevronUp, MessageSquare, Plus, X, Loader2 } from 'lucide-react';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const API_BASE =
  import.meta.env.VITE_COMMUNITY_API_URL || 'https://community-api.logpulse.io';

const CATEGORIES = [
  'All',
  'Getting Started',
  'LPQL',
  'Integrations',
  'Pipelines',
  'Feature Requests',
] as const;

type Category = (typeof CATEGORIES)[number];

interface Question {
  id: number;
  title: string;
  body: string;
  author_name: string;
  category: string;
  votes: number;
  answer_count: number;
  created_at: string;
}

interface Answer {
  id: number;
  question_id: number;
  body: string;
  author_name: string;
  votes: number;
  is_accepted: boolean;
  created_at: string;
}

interface QuestionDetail extends Question {
  answers: Answer[];
}

interface Stats {
  total_questions: number;
  total_answers: number;
  unique_authors: number;
}

const PLACEHOLDER_QUESTIONS: Question[] = [
  {
    id: 1,
    title: 'How do I filter logs by Kubernetes namespace in LPQL?',
    body: 'I am trying to narrow down my log search to a specific Kubernetes namespace using LPQL syntax but I cannot figure out the correct filter expression. Can someone help with the right query format?',
    author_name: 'DevOpsEngineer42',
    category: 'LPQL',
    votes: 5,
    answer_count: 2,
    created_at: '2026-03-15T10:30:00Z',
  },
  {
    id: 2,
    title: 'Best practices for setting up Vector agent on production?',
    body: 'We are planning to deploy Vector as our log collection agent in a production Kubernetes cluster. What are the recommended resource limits, buffer configurations, and reliability patterns that teams have found effective?',
    author_name: 'SRELead',
    category: 'Integrations',
    votes: 3,
    answer_count: 1,
    created_at: '2026-03-14T08:15:00Z',
  },
  {
    id: 3,
    title: 'Can I use LogPulse with AWS CloudWatch?',
    body: 'Our infrastructure runs on AWS and we already have CloudWatch set up. Is there a way to ingest CloudWatch logs into LogPulse, or do we need to set up a separate pipeline? Looking for integration guidance.',
    author_name: 'CloudArchitect',
    category: 'Integrations',
    votes: 8,
    answer_count: 3,
    created_at: '2026-03-12T14:45:00Z',
  },
  {
    id: 4,
    title: 'How to create a dashboard for error rate monitoring?',
    body: 'I am new to LogPulse and want to set up a dashboard that tracks error rates across our microservices. What is the best approach to get started with this?',
    author_name: 'JuniorDev',
    category: 'Getting Started',
    votes: 2,
    answer_count: 1,
    created_at: '2026-03-10T16:20:00Z',
  },
  {
    id: 5,
    title: 'Feature Request: Support for Grafana dashboards export',
    body: 'It would be incredibly useful to export LogPulse dashboards in a Grafana-compatible format. Many teams already use Grafana for metrics and having a unified dashboard experience would reduce context switching significantly.',
    author_name: 'PlatformEngineer',
    category: 'Feature Requests',
    votes: 12,
    answer_count: 4,
    created_at: '2026-03-08T11:00:00Z',
  },
];

const PLACEHOLDER_STATS: Stats = {
  total_questions: 247,
  total_answers: 892,
  unique_authors: 156,
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function categoryColor(category: string): string {
  switch (category) {
    case 'LPQL':
      return 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300';
    case 'Integrations':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300';
    case 'Getting Started':
      return 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300';
    case 'Pipelines':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300';
    case 'Feature Requests':
      return 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300';
  }
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchQuestions(
  category: Category,
  search: string,
): Promise<Question[]> {
  const params = new URLSearchParams();
  if (category !== 'All') params.set('category', category);
  if (search) params.set('search', search);
  const res = await fetch(`${API_BASE}/api/questions?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch questions');
  const data = await res.json();
  return data.questions ?? data;
}

async function fetchQuestionDetail(id: number): Promise<QuestionDetail> {
  const res = await fetch(`${API_BASE}/api/questions/${id}`);
  if (!res.ok) throw new Error('Failed to fetch question');
  return res.json();
}

async function fetchStats(): Promise<Stats> {
  const res = await fetch(`${API_BASE}/api/stats`);
  if (!res.ok) throw new Error('Failed to fetch stats');
  return res.json();
}

async function postQuestion(data: {
  title: string;
  body: string;
  author_name: string;
  category: string;
}): Promise<Question> {
  const res = await fetch(`${API_BASE}/api/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to post question');
  }
  return res.json();
}

async function postAnswer(
  questionId: number,
  data: { body: string; author_name: string },
): Promise<Answer> {
  const res = await fetch(`${API_BASE}/api/questions/${questionId}/answers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to post answer');
  }
  return res.json();
}

async function submitVote(
  type: 'questions' | 'answers',
  id: number,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/${type}/${id}/vote`, {
    method: 'POST',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to vote');
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatsBar({ stats }: { stats: Stats }) {
  const items = [
    { label: 'Questions Asked', value: stats.total_questions },
    { label: 'Answers', value: stats.total_answers },
    { label: 'Members', value: stats.unique_authors },
  ];

  return (
    <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto">
      {items.map((item) => (
        <div key={item.label} className="text-center">
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {item.value.toLocaleString()}
          </p>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            {item.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function CategoryTabs({
  active,
  onChange,
}: {
  active: Category;
  onChange: (c: Category) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {CATEGORIES.map((cat) => (
        <button
          key={cat}
          onClick={() => onChange(cat)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            active === cat
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

function QuestionCard({
  question,
  onSelect,
  onVote,
}: {
  question: Question;
  onSelect: () => void;
  onVote: () => void;
}) {
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-4 sm:p-5 hover:border-primary-300 dark:hover:border-primary-700 transition-colors bg-white dark:bg-gray-900/50">
      <div className="flex gap-4">
        {/* Vote column */}
        <div className="flex flex-col items-center gap-1 pt-0.5">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVote();
            }}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            aria-label="Upvote"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {question.votes}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onSelect}>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-snug mb-1.5">
            {question.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
            {question.body}
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor(question.category)}`}
            >
              {question.category}
            </span>
            <span>{question.author_name}</span>
            <span>--</span>
            <span>{formatDate(question.created_at)}</span>
            <span className="inline-flex items-center gap-1 ml-auto">
              <MessageSquare className="h-3.5 w-3.5" />
              {question.answer_count}{' '}
              {question.answer_count === 1 ? 'answer' : 'answers'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionDetailView({
  questionId,
  onBack,
}: {
  questionId: number;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<QuestionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Answer form state
  const [answerBody, setAnswerBody] = useState('');
  const [answerName, setAnswerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchQuestionDetail(questionId)
      .then(setDetail)
      .catch(() => {
        // Fallback to placeholder if available
        const placeholder = PLACEHOLDER_QUESTIONS.find(
          (q) => q.id === questionId,
        );
        if (placeholder) {
          setDetail({ ...placeholder, answers: [] });
        } else {
          setError('Could not load question details.');
        }
      })
      .finally(() => setLoading(false));
  }, [questionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmitAnswer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!answerBody.trim() || !answerName.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await postAnswer(questionId, {
        body: answerBody.trim(),
        author_name: answerName.trim(),
      });
      setAnswerBody('');
      load();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to post answer',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoteAnswer = async (answerId: number) => {
    try {
      await submitVote('answers', answerId);
      load();
    } catch {
      // Silently ignore vote errors (e.g. already voted)
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500 dark:text-red-400 mb-4">
          {error || 'Question not found.'}
        </p>
        <button
          onClick={onBack}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          Back to questions
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onBack}
        className="text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4 inline-block"
      >
        Back to questions
      </button>

      {/* Question */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-5 sm:p-6 bg-white dark:bg-gray-900/50 mb-6">
        <div className="flex items-start gap-2 mb-2">
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor(detail.category)}`}
          >
            {detail.category}
          </span>
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
          {detail.title}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed mb-4">
          {detail.body}
        </p>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium">{detail.author_name}</span>
          <span>{formatDate(detail.created_at)}</span>
          <span className="ml-auto font-medium">
            {detail.votes} {detail.votes === 1 ? 'vote' : 'votes'}
          </span>
        </div>
      </div>

      {/* Answers */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {detail.answers.length}{' '}
        {detail.answers.length === 1 ? 'Answer' : 'Answers'}
      </h3>

      {detail.answers.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          No answers yet. Be the first to answer this question.
        </p>
      )}

      <div className="space-y-4 mb-8">
        {detail.answers.map((answer) => (
          <div
            key={answer.id}
            className="border border-gray-200 dark:border-gray-800 rounded-xl p-4 sm:p-5 bg-white dark:bg-gray-900/50"
          >
            <div className="flex gap-3">
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={() => handleVoteAnswer(answer.id)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  aria-label="Upvote answer"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  {answer.votes}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed mb-3">
                  {answer.body}
                </p>
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">{answer.author_name}</span>
                  <span>{formatDate(answer.created_at)}</span>
                  {answer.is_accepted && (
                    <span className="ml-auto text-green-600 dark:text-green-400 font-medium">
                      Accepted
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Post answer form */}
      <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-5 sm:p-6 bg-white dark:bg-gray-900/50">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Post an Answer
        </h4>
        <form onSubmit={handleSubmitAnswer} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={answerName}
              onChange={(e) => setAnswerName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter your name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Your Answer
            </label>
            <textarea
              value={answerBody}
              onChange={(e) => setAnswerBody(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
              placeholder="Write your answer here. Markdown is supported."
              required
              minLength={10}
            />
          </div>
          {submitError && (
            <p className="text-sm text-red-500 dark:text-red-400">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit Answer
          </button>
        </form>
      </div>
    </div>
  );
}

function NewQuestionModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [category, setCategory] = useState('Getting Started');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await postQuestion({
        title: title.trim(),
        body: body.trim(),
        author_name: authorName.trim(),
        category,
      });
      setTitle('');
      setBody('');
      setAuthorName('');
      setCategory('Getting Started');
      onClose();
      onCreated();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create question',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative w-full max-w-xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Ask a Question
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter your name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="What is your question?"
              required
              minLength={3}
              maxLength={200}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {CATEGORIES.filter((c) => c !== 'All').map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Details
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
              placeholder="Describe your question in detail. Markdown is supported."
              required
              minLength={10}
              maxLength={10000}
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Post Question
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function CommunityPage() {
  useDocumentMeta({ title: 'Community — LogPulse', description: 'Join the LogPulse community on Discord, GitHub, and Twitter.' });

  useDocumentHead({
    title: 'Community',
    description:
      'Join the LogPulse community forum. Ask questions about LPQL, integrations, pipelines, and more. Share knowledge and connect with other LogPulse users.',
    url: '/community',
  });

  const [stats, setStats] = useState<Stats>(PLACEHOLDER_STATS);
  const [questions, setQuestions] = useState<Question[]>(PLACEHOLDER_QUESTIONS);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(
    null,
  );
  const [showNewQuestion, setShowNewQuestion] = useState(false);

  const loadQuestions = useCallback(() => {
    setLoading(true);
    fetchQuestions(activeCategory, searchQuery)
      .then((data) => setQuestions(data))
      .catch(() => {
        // Fall back to placeholders filtered client-side
        let filtered = PLACEHOLDER_QUESTIONS;
        if (activeCategory !== 'All') {
          filtered = filtered.filter((q) => q.category === activeCategory);
        }
        if (searchQuery) {
          const lower = searchQuery.toLowerCase();
          filtered = filtered.filter(
            (q) =>
              q.title.toLowerCase().includes(lower) ||
              q.body.toLowerCase().includes(lower),
          );
        }
        setQuestions(filtered);
      })
      .finally(() => setLoading(false));
  }, [activeCategory, searchQuery]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    fetchStats()
      .then(setStats)
      .catch(() => {
        /* keep placeholders */
      });
  }, []);

  const handleVote = async (questionId: number) => {
    try {
      await submitVote('questions', questionId);
      loadQuestions();
    } catch {
      // Silently ignore vote errors
    }
  };

  if (selectedQuestionId !== null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <LandingHeader />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-16">
          <QuestionDetailView
            questionId={selectedQuestionId}
            onBack={() => setSelectedQuestionId(null)}
          />
        </main>
        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <LandingHeader />

      {/* Preview Banner */}
      <div className="pt-20 sm:pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5 flex items-start gap-3">
            <svg className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-300">Preview — Under Active Development</p>
              <p className="text-sm text-amber-400/80 mt-1">
                This is a preview of the LogPulse Community space. We are actively building this feature and
                the data shown below is for demonstration purposes only. Want to be notified when we launch?{' '}
                <LocalizedAnchor href="/contact" className="underline hover:text-amber-300 transition-colors">Get in touch</LocalizedAnchor>.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-8 sm:pt-12 pb-10 sm:pb-14 text-center px-4">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          Community
        </h1>
        <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          Ask questions, share knowledge, and connect with other LogPulse users
        </p>
      </section>

      {/* Stats */}
      <section className="pb-10 sm:pb-14 px-4">
        <StatsBar stats={stats} />
      </section>

      {/* Controls */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CategoryTabs active={activeCategory} onChange={setActiveCategory} />
          <button
            onClick={() => setShowNewQuestion(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors self-start sm:self-auto"
          >
            <Plus className="h-4 w-4" />
            Ask a Question
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Search questions..."
          />
        </div>
      </section>

      {/* Question list */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-gray-400">
              No questions found. Be the first to ask one!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {questions.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                onSelect={() => setSelectedQuestionId(q.id)}
                onVote={() => handleVote(q.id)}
              />
            ))}
          </div>
        )}
      </section>

      <LandingFooter />

      {/* New question modal */}
      <NewQuestionModal
        open={showNewQuestion}
        onClose={() => setShowNewQuestion(false)}
        onCreated={loadQuestions}
      />
    </div>
  );
}
